import { logger } from '../../../utils/logger';
import React, { useEffect, useRef } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAppSelector } from '../../../hooks/redux';
import { Spinner } from '../../common/Spinner';
import { getMsalInstance, loginRequest } from '../../../config/authConfig';
import {
  isAuthRedirectInProgress,
  markAuthRedirectInProgress,
  clearAuthRedirectInProgress,
} from '../../../utils/authRedirect';

interface ProtectedRouteProps {
  children: React.ReactNode;
  requireAuth?: boolean;
  /** Skip onboarding check (for onboarding pages themselves) */
  skipOnboardingCheck?: boolean;
}

/**
 * Component that handles redirecting to Entra External ID for authentication
 * Shows Microsoft's native hosted sign-in page with all configured providers
 */
const EntraRedirect: React.FC = () => {
  const location = useLocation();
  const redirectAttempted = useRef(false);

  useEffect(() => {
    const redirectToEntra = async () => {
      // Prevent multiple redirect attempts
      if (redirectAttempted.current || isAuthRedirectInProgress()) {
        logger.trace('[EntraRedirect] Redirect already attempted, skipping...', {
          event: 'auth.redirect.skip',
        });
        return;
      }

      // Don't redirect if we're on the auth callback page (processing a response)
      if (location.pathname === '/auth/callback') {
        logger.trace('[EntraRedirect] On callback page, skipping redirect...', {
          event: 'auth.redirect.skip_callback',
        });
        return;
      }

      try {
        // Get already-initialized MSAL instance (initialized in index.tsx)
        const msalInstance = getMsalInstance();

        // Check if there are accounts in cache
        const allAccounts = msalInstance.getAllAccounts();

        logger.trace('[EntraRedirect] Current state', {
          event: 'auth.redirect.state',
          pathname: location.pathname,
          accountsInCache: allAccounts.length,
          redirectInProgress: isAuthRedirectInProgress(),
        });

        // If there are accounts in the cache, don't redirect - let validateSession handle it
        if (allAccounts.length > 0) {
          logger.trace('[EntraRedirect] Accounts found in MSAL cache, skipping redirect', {
            event: 'auth.redirect.skip_cached',
          });
          return;
        }

        redirectAttempted.current = true;
        markAuthRedirectInProgress();

        // Save current location for post-login redirect
        if (location.pathname !== '/' && location.pathname !== '/auth/callback') {
          sessionStorage.setItem('auth_redirect_uri', location.pathname);
        }

        logger.debug('[EntraRedirect] Initiating redirect to Entra External ID...', {
          event: 'auth.redirect.started',
        });
        logger.trace('[EntraRedirect] Login scopes', {
          event: 'auth.redirect.scopes',
          scopes: loginRequest.scopes,
        });

        // Trigger MSAL redirect to Entra's hosted sign-in page
        await msalInstance.loginRedirect(loginRequest);
      } catch (error: any) {
        // interaction_in_progress means another loginRedirect is already in
        // flight (e.g. kicked off by a concurrent component). Safe to ignore —
        // the in-flight redirect will complete and land on the sign-in page.
        if (error?.errorCode === 'interaction_in_progress') {
          logger.trace('[EntraRedirect] Another redirect already in progress, skipping...', {
            event: 'auth.redirect.in_progress',
          });
          return;
        }
        logger.error('[EntraRedirect] Failed to initiate login redirect:', error);
        clearAuthRedirectInProgress(); // Reset on error to allow retry
      }
    };

    redirectToEntra();
  }, [location.pathname]);

  // Show loading screen while redirect is being initiated
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-50">
      <div className="text-center">
        <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
        <p className="text-lg text-gray-600">Redirecting to sign in...</p>
      </div>
    </div>
  );
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAuth = true,
  skipOnboardingCheck = false,
}) => {
  const location = useLocation();
  const { isAuthenticated, isLoading, isInitialized, onboardingRequired } = useAppSelector(
    (state) => state.auth
  );

  // Show loading spinner during initialization or loading
  if (isLoading || !isInitialized) {
    logger.trace('ProtectedRoute waiting for auth init', {
      event: 'auth.route.waiting',
      isLoading,
      isInitialized,
    });
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Spinner size="lg" />
      </div>
    );
  }

  // Only make redirect decisions after initialization is complete
  if (requireAuth && !isAuthenticated) {
    logger.trace('ProtectedRoute requiring auth redirect', {
      event: 'auth.route.unauthenticated',
      pathname: location.pathname,
    });
    // Trigger direct redirect to Entra External ID instead of showing custom login page
    return <EntraRedirect />;
  }

  if (!requireAuth && isAuthenticated) {
    // If user is already authenticated and tries to access auth pages (login/register)
    // redirect them to dashboard (or onboarding if needed)
    if (onboardingRequired) {
      logger.trace('ProtectedRoute redirecting authenticated user to onboarding', {
        event: 'auth.route.to_onboarding',
      });
      return <Navigate to="/onboarding/select-plan" replace />;
    }
    logger.trace('ProtectedRoute redirecting authenticated user to dashboard', {
      event: 'auth.route.to_dashboard',
    });
    return <Navigate to="/dashboard" replace />;
  }

  // Check if user needs to complete onboarding (select SKU)
  // Skip this check for onboarding pages themselves to avoid infinite redirect
  if (
    requireAuth &&
    isAuthenticated &&
    onboardingRequired &&
    !skipOnboardingCheck &&
    !location.pathname.startsWith('/onboarding')
  ) {
    logger.trace('ProtectedRoute requiring onboarding', {
      event: 'auth.route.onboarding_required',
    });
    return <Navigate to="/onboarding/select-plan" replace />;
  }

  return <>{children}</>;
};
